import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { AlertTriangle, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";

export const DangerZone = () => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const target = t("del_word");

  const del = async () => {
    setBusy(true);
    try {
      await api.delete("/account/me", { data: { confirm: "DELETE" } });
      toast.success(t("del_done"));
      try { await logout(); } catch { void 0; }
      navigate("/");
    } catch (err) { toast.error(errorMessage(err, t)); setBusy(false); }
  };

  return (
    <div className="mt-12 rounded-2xl border border-red-500/25 bg-red-500/[0.03] p-6" data-testid="danger-zone">
      <h2 className="text-lg font-bold flex items-center gap-2 text-red-300"><AlertTriangle className="w-5 h-5" /> {t("dz_title")}</h2>

      <div className="mt-4 rounded-xl bg-white/[0.03] border border-white/10 p-4" data-testid="retention-info">
        <div className="text-sm font-semibold flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-orange-400" /> {t("ret_title")}</div>
        <ul className="text-sm text-zinc-400 space-y-1.5 list-disc pl-5">
          <li>{t("ret_media")}</li>
          <li>{t("ret_prompts")}</li>
          <li>{t("ret_external")}</li>
          <li>{t("ret_back")}</li>
        </ul>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <p className="text-sm text-zinc-400 max-w-xl">{t("del_intro")}</p>
        <Button onClick={() => { setWord(""); setOpen(true); }} disabled={user?.role === "admin"} data-testid="delete-account-btn"
          className="rounded-full bg-red-600 hover:bg-red-700 text-white shrink-0">
          <Trash2 className="w-4 h-4 mr-2" /> {t("del_btn")}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121218] border-red-500/30 text-white max-w-lg" data-testid="delete-account-dialog">
          <DialogHeader><DialogTitle className="text-red-300">{t("del_confirm_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-300">{t("del_lost_title")}</p>
          <ul className="text-sm text-zinc-400 space-y-1 list-disc pl-5">
            <li>{t("del_lost_1")}</li>
            <li>{t("del_lost_2")}</li>
            <li>{t("del_lost_3")}</li>
            <li>{t("del_lost_4")}</li>
          </ul>
          <p className="text-xs text-zinc-500">{t("del_kept")}</p>
          <label className="text-sm text-zinc-300 mt-2">{t("del_type").replace("{w}", target)}</label>
          <input value={word} onChange={(e) => setWord(e.target.value)} data-testid="delete-confirm-input"
            className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-red-400" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400 hover:bg-white/5" data-testid="delete-cancel-btn">{t("cancel")}</Button>
            <Button onClick={del} disabled={busy || word.trim().toUpperCase() !== target.toUpperCase()} data-testid="delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? "..." : t("del_final")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
